import React, { useState, useEffect, useRef } from "react";
import PreviaPedidos from "./PreviaPedidos";
import Nav from "./Nav";
import Paginador from "./Paginador";
import PedidoForm from "./PedidoForm";
import {
  collection,
  deleteDoc,
  getFirestore,
  doc,
  getDocs,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { useNavContext } from "../context/NavContext";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "react-modal";
import moment from "moment";
import Spinner from "./Spinner";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Breadcrumb from "./Breadcrumb";
import useFileUpload from "../hooks/useFileUpload";

// helpers para quitar acentos y pasar a minúsculas
const normalize = (str) =>
  str
    .toLowerCase()
    .normalize("NFD") // descompone acentos
    .replace(/[\u0300-\u036f]/g, ""); // elimina marcas diacríticas

moment.locale("es");

const styleForm = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingLeft: 0,
    padding: 0,
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
};

const customStyles = {
  content: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    padding: "0",
    border: "none",
    borderRadius: "0", // Si quieres sin bordes redondeados para pantalla completa
    boxShadow: "none",
    width: "100vw",
    height: "100vh",
    overflow: "auto",
    fontFamily: "Inter, sans-serif",
    /* Opciones para ocultar scrollbars */
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1008,
  },
};

function TablaPedidos({
  ordenes,
  clientes,
  setOrdenes,
  guardarOrden,
  modoEdicion,
}) {
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const { formatDate } = useFunctionsPrevia();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [loadingOrdenes, setLoadingOrdenes] = useState(true);
  const [selectedOrdenes, setSelectedOrdenes] = useState([]);
  const [ordenamiento, setOrdenamiento] = useState({
    campo: "fechaOrden",
    ascendente: false,
  });
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [showNuevoButton, setShowNuevoButton] = useState(true);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [estadosCeldas, setEstadosCeldas] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [formDirty, setFormDirty] = useState(false);
  const [cotizacionParaOrden, setCotizacionParaOrden] = useState(null);
  const handleFileUpload = useFileUpload({
    ownerUid, // tu uid del usuario/equipo
    setData: setOrdenes, // setter para actualizar el estado local
    docType: "pedidos", // tipo de documento según docConfigs
  });
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const pedidosRef = getSubCollection(ownerUid, "pedidos");

    const unsubscribe = onSnapshot(pedidosRef, async (snapshot) => {
      const ordersWithItems = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const productosSnap = await getDocs(
            collection(
              getFirestore(),
              "usuarios",
              ownerUid,
              "pedidos",
              docSnap.id,
              "productos"
            )
          );
          const productos = productosSnap.docs.map((p) => ({
            id: p.id,
            ...p.data(),
          }));
          return { id: docSnap.id, ...data, productos };
        })
      );
      setOrdenes(ordersWithItems);
      setLoadingOrdenes(false);
    });

    return () => unsubscribe();
  }, [setOrdenes, ownerUid]);

  useEffect(() => {
    if (location.state && location.state.openModal) {
      setMostrarFormulario(true);
    }
  }, [location]);

  useEffect(() => {
    const convertir = location.state?.convertirEnOrden;
    const cot = location.state?.cotizacion;

    if (convertir && cot) {
      setCotizacionParaOrden(cot);
      setMostrarFormulario(true);
      navigate("/pedidos", { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEditar = (orden) => {
    // Cierra la vista previa
    setModalIsOpen(false);
    // Selecciona la orden que vamos a editar
    setOrdenSeleccionada(orden);
    // Abre el formulario
    setMostrarFormulario(true);
    setShowNuevoButton(false);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
  };

  const toggleOptionsMenu = () => {
    setShowOptionsMenu((prevShowOptionsMenu) => !prevShowOptionsMenu);
  };

  const handleImportClick = () => {
    fileInputRef.current.click(); // Simula el clic en el input de archivo
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFormDirtyChange = (dirty) => {
    setFormDirty(dirty);
  };

  // Función para alternar estado y guardarlo en Firestore
  const toggleEstado = async (ordenId) => {
    const newEstado =
      estadosCeldas[ordenId] === "Pendiente"
        ? "En Proceso"
        : estadosCeldas[ordenId] === "En Proceso"
        ? "Facturado"
        : estadosCeldas[ordenId] === "Facturado"
        ? "Pagada"
        : "Pendiente";

    setEstadosCeldas((prev) => ({
      ...prev,
      [ordenId]: newEstado,
    }));

    const pedidosRef = getSubCollection(ownerUid, "pedidos");
    const pedidoDocRef = doc(pedidosRef, ordenId);
    await updateDoc(pedidoDocRef, { estado: newEstado });
  };

  const openFormulario = () => {
    setOrdenSeleccionada(null);
    setCotizacionParaOrden(null);
    setMostrarFormulario(true);
    setShowNuevoButton(false);
  };

  const closeModalFormulario = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          setMostrarFormulario(false);
          setShowNuevoButton(true);
          setFormDirty(false);
        }
      });
    } else {
      setMostrarFormulario(false);
      setShowNuevoButton(true);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      const confirmed = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará las órdenes seleccionadas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });

      if (!confirmed.isConfirmed) return;

      const ordenesRef = getSubCollection(ownerUid, "pedidos");
      const ordenesEliminadas = [];

      for (const ordenId of selectedOrdenes) {
        const ordenDocRef = doc(ordenesRef, ordenId);
        await deleteDoc(ordenDocRef);
        ordenesEliminadas.push(ordenId);
      }

      setOrdenes((prev) =>
        prev.filter((o) => !ordenesEliminadas.includes(o.id))
      );
      setSelectedOrdenes([]);

      await Swal.fire({
        title: "¡Orden eliminada!",
        text: "Las órdenes seleccionadas han sido eliminadas correctamente.",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
    } catch (error) {
      console.error("Error al eliminar órdenes:", error);
    }
  };

  // Al hacer clic en la fila, abrimos el modal con los detalles
  const handleRowClick = (orden) => {
    setOrdenSeleccionada(orden);
    setModalIsOpen(true);
  };

  const cerrarModalPrevia = () => {
    setModalIsOpen(false);
    setOrdenSeleccionada(null);
  };

  const handleSelectOrden = (ordenId) => {
    setSelectedOrdenes((prevSelected) => {
      if (prevSelected.includes(ordenId)) {
        return prevSelected.filter((id) => id !== ordenId);
      } else {
        return [...prevSelected, ordenId];
      }
    });
  };

  const handleOrdenamientoChange = (campo) => {
    setOrdenamiento((prevOrdenamiento) => ({
      campo,
      ascendente:
        campo === prevOrdenamiento.campo ? !prevOrdenamiento.ascendente : true,
    }));
  };

  const ordenarOrdenes = (ordenes, { campo, ascendente }) => {
    if (!ordenes || ordenes.length === 0) {
      return [];
    }
    const sortedOrdenes = [...ordenes];
    switch (campo) {
      case "fechaOrden":
        sortedOrdenes.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            (new Date(a.fechaOrden) - new Date(b.fechaOrden))
        );
        break;
      case "numeroOrdenDeCompra":
        sortedOrdenes.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            (a.numeroOrdenDeCompra - b.numeroOrdenDeCompra)
        );
        break;
      case "nombreProveedor":
        sortedOrdenes.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            a.nombreProveedor.localeCompare(b.nombreProveedor)
        );
        break;
      case "total":
        sortedOrdenes.sort(
          (a, b) => (ascendente ? 1 : -1) * (a.total - b.total)
        );
        break;
      case "fechaVencimiento":
        sortedOrdenes.sort(
          (a, b) =>
            (ascendente ? 1 : -1) *
            (new Date(a.fechaVencimiento) - new Date(b.fechaVencimiento))
        );
        break;
      default:
        break;
    }
    return sortedOrdenes;
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allOrdenesIds = filteredOrdenes.map((orden) => orden.id);
      setSelectedOrdenes(allOrdenesIds);
    } else {
      setSelectedOrdenes([]);
    }
  };

  const filteredOrdenes = React.useMemo(() => {
    if (!ordenes || ordenes.length === 0) return [];

    // helper para fechas (igual que antes)
    const generarFechas = (fecha) => {
      if (!fecha) return [];
      const d = new Date(fecha);
      return [
        normalize(
          d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        ),
        normalize(
          d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        ),
      ];
    };

    // normaliza y separa la búsqueda
    const searchWords = normalize(searchTerm).split(" ").filter(Boolean);

    return ordenes.filter((orden) => {
      const baseFields = [
        orden.numeroOrdenDeCompra?.toString(),
        orden.referencia,
        orden.nombreProveedor,
        orden.nombreCliente,
        orden.estado,
        orden.vendedorNombre,
        orden.total?.toString(),
        orden.divisa,
        ...generarFechas(orden.fechaOrden),
        ...generarFechas(orden.fechaFinRango),
        ...generarFechas(orden.fechaCotizacion),
      ].map((f) => normalize(f || ""));

      const items = orden.productosSeleccionados ?? orden.productos ?? [];
      const productosFields = items.map((p) =>
        normalize(p.nombre || p.descripcion || "")
      );

      const partidasField = (orden.productos?.length || 0).toString();

      const searchableFields = [
        ...baseFields,
        ...productosFields,
        partidasField,
      ];

      return searchWords.every((word) =>
        searchableFields.some((field) => field.includes(word))
      );
    });
  }, [ordenes, searchTerm]);

  const paginatedOrdenes = React.useMemo(() => {
    const ordenesOrdenadas = ordenarOrdenes(filteredOrdenes, ordenamiento);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return ordenesOrdenadas.slice(startIndex, endIndex);
  }, [filteredOrdenes, currentPage, itemsPerPage, ordenamiento]);

  const totalOrdenes = filteredOrdenes.length;

  return (
    <>
      {/* Barra de búsqueda y botones */}
      <div className="no-sticky">
        {!mostrarFormulario && (
          <>
            <Nav
              handleSearch={setSearchTerm}
              proximasAVencer={proximasAVencer}
              proximosEventos={proximosEventos}
            />

            <div className="task-filters" style={{ flexWrap: "wrap" }}>
              <div
                style={{
                  flexBasis: "100%", // ocupa toda la fila
                  padding: "0 4px", // mismo gutter que usas
                  margin: "0 0 5px", // respirito arriba/abajo
                }}
              >
                <Breadcrumb />
              </div>
              <div className="header-actions-proyectos">
                <button className="action-button-proyectos">
                  <i className="bi bi-funnel-fill"></i> Todos
                  <i
                    className="bi bi-chevron-down"
                    style={{ fontSize: "12px" }}
                  ></i>
                </button>
                <div className="buscador-pedidos">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    className="buscador-input"
                    placeholder="Buscar pedidos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="task-filters-right">
                <div
                  className="view-buttons-container"
                  style={{ position: "relative" }}
                >
                  <div className="delete-button-container">
                    <button
                      type="button"
                      className="delete-button"
                      style={{
                        visibility:
                          selectedOrdenes.length > 0 ? "visible" : "hidden",
                        opacity: selectedOrdenes.length > 0 ? 1 : 0,
                        transform:
                          selectedOrdenes.length > 0
                            ? "scale(1)"
                            : "scale(0.9)",
                        transition:
                          "visibility 0s linear 0.2s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                      }}
                      onClick={handleDeleteSelected}
                    >
                      Eliminar
                    </button>
                  </div>
                  <button className="view-button" onClick={toggleOptionsMenu}>
                    <i className="bi bi-three-dots-vertical"></i>
                  </button>
                  {showOptionsMenu && (
                    <div className="dropdown-options" ref={dropdownRef}>
                      <p onClick={handleImportClick}>
                        <i
                          className="bi bi-box-arrow-in-down"
                          style={{ marginRight: 10 }}
                        ></i>
                        Importar
                      </p>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".xlsx, .xls"
                        onChange={handleFileUpload}
                      />
                      <p>
                        <i
                          className="bi bi-box-arrow-up"
                          style={{ marginRight: 10 }}
                        ></i>
                        Exportar
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  {showNuevoButton && (
                    <button
                      className="new-task-button"
                      onClick={openFormulario}
                    >
                      + Crear Nuevo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="cotizaciones-table">
          <div className="tabla-cotizaciones-wrapper">
            <div className="tabla-cotizaciones-content">
              {loadingOrdenes ? (
                <Spinner />
              ) : (
                <>
                  {/** — Si NO hay ninguna orden cargada */}
                  {ordenes.length === 0 ? (
                    <div className="no-resultados">
                      <div
                        className="mensaje-sin-resultados"
                        style={{ marginTop: "30px" }}
                      >
                        <p style={{ fontSize: "14px" }}>
                          Aún no hay ninguna orden de compra.
                        </p>
                      </div>
                      <div className="imagen-sin-resultados">
                        <img
                          src="/img/no-data.svg"
                          alt="Sin órdenes"
                          width={350}
                          height={350}
                        />
                      </div>
                    </div>
                  ) : filteredOrdenes.length > 0 ? (
                    /** — Si hay resultados tras el filtrado, muestro la tabla */
                    <table>
                      {!mostrarFormulario && (
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                className="checkbox-select-all check"
                                checked={
                                  selectedOrdenes.length ===
                                  filteredOrdenes.length
                                }
                              />
                            </th>
                            <th
                              onClick={() =>
                                handleOrdenamientoChange("fechaOrden")
                              }
                            >
                              Fecha
                            </th>
                            <th
                              onClick={() =>
                                handleOrdenamientoChange("numeroOrdenDeCompra")
                              }
                            >
                              No. Orden
                            </th>
                            <th
                              onClick={() =>
                                handleOrdenamientoChange("fechaDeEntrega")
                              }
                            >
                              Fecha de Entrega
                            </th>
                            <th
                              onClick={() =>
                                handleOrdenamientoChange("nombreCliente")
                              }
                            >
                              Cliente
                            </th>
                            <th
                              onClick={() => handleOrdenamientoChange("estado")}
                            >
                              Estado
                            </th>
                            <th
                              onClick={() => handleOrdenamientoChange("total")}
                            >
                              Importe
                            </th>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {paginatedOrdenes.map((orden) => (
                          <tr key={orden.id}>
                            <td className="check">
                              <input
                                type="checkbox"
                                checked={selectedOrdenes.includes(orden.id)}
                                onChange={() => handleSelectOrden(orden.id)}
                                className="checkbox"
                              />
                            </td>
                            <td onClick={() => handleRowClick(orden)}>
                              <div className="estilos-fechas">
                                {formatDate(orden.fechaOrden)}
                              </div>
                            </td>
                            <td
                              style={{ color: "#007bff", fontWeight: "500" }}
                              onClick={() => handleRowClick(orden)}
                            >
                              {orden.numeroOrdenDeCompra
                                ?.toString()
                                .padStart(4, "0")}
                            </td>
                            <td onClick={() => handleRowClick(orden)}>
                              {orden.fechaFinRango
                                ? new Date(
                                    orden.fechaFinRango
                                  ).toLocaleDateString("es-ES", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "N/A"}
                            </td>
                            <td onClick={() => handleRowClick(orden)}>
                              {orden.nombreCliente}
                            </td>
                            <td>
                              <div
                                className={`status ${
                                  estadosCeldas[orden.id] === "Pendiente"
                                    ? "pendiente"
                                    : estadosCeldas[orden.id] === "En Proceso"
                                    ? "en-proceso"
                                    : estadosCeldas[orden.id] === "Facturado"
                                    ? "facturado"
                                    : estadosCeldas[orden.id] === "Pagada"
                                    ? "pagada"
                                    : "pendiente"
                                }`}
                                onClick={() => toggleEstado(orden.id)}
                              >
                                {estadosCeldas[orden.id] || "Pendiente"}
                              </div>
                            </td>
                            <td
                              className="importe"
                              onClick={() => handleRowClick(orden)}
                            >
                              {orden.divisa}{" "}
                              {parseFloat(orden.total)
                                .toFixed(2)
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    /** — Si no hay coincidencias en la búsqueda */
                    <div className="no-resultados">
                      <div
                        className="mensaje-sin-resultados"
                        style={{ marginTop: "30px" }}
                      >
                        <p style={{ fontSize: "14px" }}>
                          No existe ninguna orden de compra que coincida con
                          esta búsqueda.
                        </p>
                      </div>
                      <div className="imagen-sin-resultados">
                        <img
                          src="/img/noSearch.svg"
                          alt="Sin resultados"
                          className="sin-resultados-imagen"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Modal del formulario de creación/edición */}
              <Modal
                isOpen={mostrarFormulario}
                onRequestClose={closeModalFormulario}
                contentLabel="Orden de Compra"
                style={styleForm}
              >
                <button
                  onClick={closeModalFormulario}
                  className="cerrar-button"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
                <PedidoForm
                  clientes={clientes}
                  guardarOrden={guardarOrden}
                  modoEdicion={modoEdicion}
                  cotizacion={cotizacionParaOrden}
                  orden={ordenSeleccionada}
                  onFormDirtyChange={handleFormDirtyChange}
                  closeModal={closeModalFormulario}
                />
              </Modal>
            </div>

            <div className="tabla-cotizaciones-footer">
              <div className="paginador-container">
                <Paginador
                  totalItems={totalOrdenes}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>
          </div>

          {/* Modal para vista previa / detalles del pedido */}
          <Modal
            isOpen={modalIsOpen}
            onRequestClose={cerrarModalPrevia}
            contentLabel="Vista Previa"
            style={customStyles}
          >
            {ordenSeleccionada && (
              <PreviaPedidos
                orden={ordenSeleccionada}
                numeroOrden={ordenSeleccionada.numeroOrdenDeCompra}
                clientes={clientes}
                cerrarPrevia={cerrarModalPrevia}
                onEditar={handleEditar}
              />
            )}
          </Modal>
        </div>
      </div>
    </>
  );
}

export default TablaPedidos;
